"use client";

/**
 * Tela de confirmação exibida no lugar do wizard depois que o agendamento é
 * criado.
 *
 * Era um modal (`fixed inset-0` sem `max-h`/scroll interno): em telas baixas
 * o conteúdo estourava o viewport e o botão "Agendar outro horário" ficava
 * inalcançável. Como conteúdo de página normal ela cresce e rola junto com o
 * resto — sem overlay, sem limite de altura para administrar.
 */

import Link from "next/link";
import type { BookingData } from "../types";
import { formatBRL } from "../../../lib/currency";
import { formatDurationMinutes } from "../../../lib/time";
import { buildGoogleCalendarUrl, downloadIcsFile } from "../utils/calendar";
import {
  FiArrowRight,
  FiCheckCircle,
  FiMail,
  FiSmartphone,
  FiUserPlus,
} from "react-icons/fi";
import { InstallAppAction } from "../../../components/app/pwa/InstallAppAction";
import { usePwaInstall } from "../../../components/app/pwa/PwaInstallProvider";
import { getShortInstruction } from "../../../lib/pwa/installSteps";

interface BookingConfirmationScreenProps {
  booking: BookingData;
  /** Nome do salão no convite de instalação (CONV-03). */
  establishmentName: string;
  successMessage: string | null;
  isCustomerLoggedIn: boolean;
  customerAreaHref: string;
  accountSignupHref: string;
  onBookAnother: () => void;
}

export function BookingConfirmationScreen({
  booking,
  establishmentName,
  successMessage,
  isCustomerLoggedIn,
  customerAreaHref,
  accountSignupHref,
  onBookAnother,
}: BookingConfirmationScreenProps) {
  const { isInstalled, hasInstallPath, canPromptNatively, platform } = usePwaInstall();
  // CONV-06 e INST-07: o bloco inteiro some, não só o botão.
  const showInstallInvite = !isInstalled && hasInstallPath;

  return (
    <section
      role="status"
      aria-labelledby="booking-confirmation-title"
      className="animate-fade-in rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-8"
    >
      <div className="mx-auto max-w-xl text-center">
        <div className="animate-scale-in mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-3xl text-accent-700">
          <FiCheckCircle aria-hidden="true" />
        </div>
        <h2
          id="booking-confirmation-title"
          className="mt-4 text-2xl font-bold leading-tight text-[#233055] sm:text-3xl"
        >
          Agendamento confirmado!
        </h2>
        {successMessage && (
          <p className="mt-2 text-sm leading-6 text-slate-600">{successMessage}</p>
        )}
      </div>

      <div className="mx-auto mt-6 max-w-xl space-y-3.5 text-left">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-500">Data e horário</p>
          <p className="font-bold text-slate-950">
            {new Date(booking.date + "T00:00:00").toLocaleDateString("pt-BR", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}{" "}
            às {booking.time}
          </p>
        </div>

        {booking.serviceName && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-500">Serviço</p>
            <p className="font-bold text-slate-950">
              {booking.serviceName}
              {booking.serviceDuration
                ? ` · ${formatDurationMinutes(booking.serviceDuration)}`
                : ""}
              {typeof booking.price === "number" ? ` · ${formatBRL(booking.price)}` : ""}
            </p>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-500">Contato</p>
          <div className="text-slate-900">
            <p className="m-0">
              <span className="font-medium">Nome:</span> {booking.name}
            </p>
            <p className="m-0">
              <span className="font-medium">E-mail:</span> {booking.email}
            </p>
            {booking.whatsapp && (
              <p className="m-0">
                <span className="font-medium">WhatsApp:</span> {booking.whatsapp}
              </p>
            )}
          </div>
        </div>

        {booking.notes && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-500">Observações</p>
            <p className="whitespace-pre-wrap text-slate-900">{booking.notes}</p>
          </div>
        )}

        <div className="rounded-2xl border border-primary-200 bg-primary-50 p-4">
          <p className="mb-2 text-sm font-bold text-primary-700">
            Adicionar ao seu calendário
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={buildGoogleCalendarUrl(booking)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-accent-600 px-3 text-sm font-bold text-white transition hover:bg-accent-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-accent-100"
            >
              Adicionar no Google Calendar
            </a>
            <button
              type="button"
              onClick={() => downloadIcsFile(booking)}
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-primary-300 bg-white px-3 text-sm font-bold text-primary-700 transition hover:bg-primary-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
            >
              Baixar arquivo .ics
            </button>
          </div>
        </div>

        {isCustomerLoggedIn ? (
          <Link
            href={customerAreaHref}
            className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 transition hover:border-primary-300 hover:bg-primary-50"
          >
            <span className="min-w-0">
              <span className="block text-sm font-bold text-slate-900">
                Ver meus agendamentos
              </span>
              <span className="block text-sm text-slate-600">
                Acompanhe, reagende ou cancele pela sua área do cliente.
              </span>
            </span>
            <FiArrowRight
              className="h-4 w-4 flex-shrink-0 text-primary-600"
              aria-hidden="true"
            />
          </Link>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white text-primary-700 ring-1 ring-primary-100">
                <FiMail className="h-4 w-4" aria-hidden="true" />
              </span>
              <p className="min-w-0 text-sm leading-6 text-slate-700">
                Confira seu e-mail: enviamos a confirmação com o link para acompanhar,
                reagendar ou cancelar este agendamento.
              </p>
            </div>
            <Link
              href={accountSignupHref}
              className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-primary-200 bg-white p-3 transition hover:bg-primary-50"
            >
              <span className="inline-flex items-center gap-2 text-sm font-bold text-primary-700">
                <FiUserPlus className="h-4 w-4" aria-hidden="true" />
                Criar conta para acompanhar sem digitar de novo
              </span>
              <FiArrowRight className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            </Link>
          </div>
        )}

        {showInstallInvite ? (
          <div
            data-testid="booking-install-invite"
            className="rounded-2xl border border-slate-200/80 bg-slate-50/80 p-4"
          >
            <div className="flex items-start gap-3">
              <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
                <FiSmartphone className="h-4 w-4" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-900">
                  Instale o app de {establishmentName}
                </p>
                <p className="mt-0.5 text-sm leading-6 text-slate-600">
                  Tenha o {establishmentName} na tela inicial e agende de novo com um
                  toque
                </p>
                {/* INST-01: sem prompt nativo, a instrução fica visível no bloco.
                    Com prompt, o botão faz a instalação (INST-06). */}
                {canPromptNatively ? null : (
                  <p
                    data-testid="booking-install-instruction"
                    className="mt-2 text-xs leading-5 text-slate-500 sm:text-sm"
                  >
                    {getShortInstruction(platform)}
                  </p>
                )}
              </div>
            </div>
            <InstallAppAction
              variant="outline"
              label="Instalar"
              className="mt-3 w-full"
            />
          </div>
        ) : null}
      </div>

      <div className="mx-auto mt-6 max-w-xl">
        <button
          type="button"
          onClick={onBookAnother}
          className="inline-flex min-h-[3rem] w-full items-center justify-center rounded-2xl bg-primary-600 px-6 py-3 text-sm font-bold leading-none text-white shadow-md shadow-primary-600/20 transition hover:bg-primary-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
        >
          Agendar outro horário
        </button>
      </div>
    </section>
  );
}
